import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { BuchungsheldLitElementHost } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import type { Bundle } from '@adornis/buchungsheld-db/entities/Bundle.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import type { Product } from '@adornis/buchungsheld-db/entities/products.js';
import type {
  RegistrationData,
  RegistrationEinzelberatungData,
} from '@adornis/buchungsheld-db/entities/RegistrationDataBuffer.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { html, nothing } from 'lit';
import { DateTime } from 'luxon';
import type { BHRegistrationFlowSubPage } from './bh-page-registration-sub-page.js';

export function renderSelectedWorkshops(
  host: BHRegistrationFlowSubPage,
  selectedProducts: Product[],
  registrationData: RegistrationData,
) {
  return html`
    <x-flex space="sm">
      <tr-text small tone="subtle"> ${host._translation.translate('REGISTRATION', 'selected_appointments')} </tr-text>
      <x-flex space="sm" horizontal crossaxis-start wrap>
        ${selectedProducts.map(product => {
          const timeslotID =
            product instanceof Einzelberatung
              ? (
                  registrationData.productData.find(productData => productData.productID === product._id) as
                    | RegistrationEinzelberatungData
                    | undefined
                )?.timeslotID
              : null;
          const timeslot =
            product instanceof Einzelberatung && timeslotID
              ? product.slots.find(slot => slot._id === timeslotID)
              : null;
          const invalidProduct = !timeslot && product instanceof Einzelberatung;
          const getSubtext = () => {
            if (product instanceof Einzelberatung) {
              if (!timeslotID) return nothing;
              if (!timeslot) return host._translation.translate('REGISTRATION', 'invalid_appointment_selected');

              return `
                  ${host._translation.translate('on')} ${timeslot.start.toLocaleString(
                DateTime.DATE_SHORT,
              )} ${host._translation.translate('from')}
                  ${timeslot.start.toLocaleString(DateTime.TIME_SIMPLE)} ${host._translation.translate('till')}
                  ${timeslot.end.toLocaleString(DateTime.TIME_SIMPLE)}
            `;
            }
            if (product instanceof Workshop) {
              const dateStart = product.start?.toLocaleString(DateTime.DATE_SHORT);
              const dateEnd = product.end?.toLocaleString(DateTime.DATE_SHORT);
              const start = product.start?.toLocaleString(DateTime.TIME_SIMPLE);
              const end = product.end?.toLocaleString(DateTime.TIME_SIMPLE);
              if (dateStart === dateEnd)
                return `
                  ${host._translation.translate('on')} ${dateStart} 
                  ${host._translation.translate('from')} ${start}
                  ${host._translation.translate('till')} ${end}
                `;
              else
                return `
                  ${host._translation.translate('from')} ${dateStart} ${start}
                  ${host._translation.translate('till')} ${dateEnd} ${end}
                `;
            }
          };
          return html`
            <x-chip
              ${css({
                background: invalidProduct ? host.colors.tone.error.shade(300) : host.colors.accent.shade(200),
                padding: '12px',
                borderRadius: '12px',
                whiteSpace: 'normal',
              })}
            >
              <x-flex space="xs">
                <x-h4 bold> ${product.name} </x-h4>
                <tr-text> ${getSubtext()} </tr-text>
              </x-flex>
            </x-chip>
          `;
        })}

        <x-button
          mode="text"
          icon="west"
          @click=${() => host._registrationController.previous()}
          ${css({ marginTop: '14px' })}
        >
          ${host._translation.translate('REGISTRATION', 'change_selection')}
        </x-button>
      </x-flex>
    </x-flex>
  `;
}

export function renderSelectedBundles(
  host: BuchungsheldLitElementHost,
  bundles: NarrowedEntity<Bundle, { name: 1 }>[],
) {
  return html`
    <tr-flex space="sm">
      <tr-text small tone="subtle"> ${host._translation.translate('REGISTRATION', 'chosen_bundles')} </tr-text>
      <tr-flex space="sm" horizontal crossaxis-start wrap>
        ${bundles.map(
          bundle => html` <tr-chip ${css({ background: host.colors.accent.shade(200) })}> ${bundle.name} </tr-chip> `,
        )}
      </tr-flex>
    </tr-flex>
  `;
}

export const checkForAnyInvalidSelectedProducts = (products: Product[], registrationData: Maybe<RegistrationData>) => {
  return products.some(product => {
    if (product instanceof Einzelberatung) {
      const timeslotID = (
        registrationData?.productData.find(productData => productData.productID === product._id) as
          | RegistrationEinzelberatungData
          | undefined
      )?.timeslotID;
      const timeslot = timeslotID ? product.slots.find(slot => slot._id === timeslotID) : null;
      return !timeslot;
    }
    return false;
  });
};
